import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getSize, getTile, getCharPoints, moveChar, clickTile, clickTileBackground, cleanTiles, shuffleTiles, getPlayer, getPlayerNOsSorted, numLogMessages, getLogMessage, getTilesLeft, getDefLetters, getCurrentPlayerNo, gameSlice, getCanPlayTiles, getExistsCurrentChars, getShowLeave, getLocalPlayerNo, getCanMoveTiles, keypress, getPaused } from './gameSlice';
import tileBackgroundEnum from './tileBackgroundEnum';
import { useSelector, shallowEqual } from 'react-redux';
// import useResizeObserver from "use-resize-observer";
import classNames from "classnames";
import { useDrag, useDrop } from 'react-dnd';
import draggableEnum from './draggableEnum';
import { useDispatch } from 'react-redux';
import { TileDefs, TileNone, TileBackground, TileBackgroundYellow, TileBackgroundRed, TileBackgroundBrown, TileBackgroundGrey, TileBackgroundStart, Tile, TileMarked, TileJoker, TileJokerMarked } from './Tile';
import { Message } from './Message';
import messageEnum from './messageEnum';
import { useTranslation, Trans } from 'react-i18next';
import ScrollToBottom from '../../components/ScrollToBottom';
import { isMobile } from 'react-device-detect';
import { Button } from '../../components/Button';
import { ChatSubmit } from '../../components/ChatSubmit';
import { viewSlice } from '../view/viewSlice';
import { DialogType } from '../../DialogHandler';

import './Board.css';

const TileBackgroundWrapper = React.memo((props) => {
  const dispatch = useDispatch();

  const backgroundTile = (() => {
    switch (props.type) {
      case tileBackgroundEnum.NONE:
        return <TileNone/>;
      case tileBackgroundEnum.NORMAL:
        return <TileBackground />;
      case tileBackgroundEnum.STAR:
        return <TileBackgroundStart start="START" />;
      case tileBackgroundEnum.WORD_2X:
        return <TileBackgroundYellow line1="2x" line2="Ord" />;
      case tileBackgroundEnum.WORD_3X:
        return <TileBackgroundRed line1="3x" line2="Ord" />;
      case tileBackgroundEnum.CHAR_2X:
        return <TileBackgroundBrown line1="2x" line2="Bok" />;
      case tileBackgroundEnum.CHAR_3X:
        return <TileBackgroundGrey line1="3x" line2="Bok" />;
      default:
        return null;
    }
  })();

  const onClick = (props.type === tileBackgroundEnum.NONE) ? null : () => dispatch(clickTileBackground({pos: {type: props.posType, x: props.x, y: props.y}}));
  return (
    <div className="boardTile" onClick={onClick}>
      {backgroundTile}
    </div>
  );
});

const TileWrapper = (props) => {
  const classes = classNames({
    "tile": true,
    "played": props.played,
    "current": !props.played,
  });
  const points = useSelector(state => getCharPoints(state, {char: props.char}));
  const maybeMarkedTile = (props.marked)
    ? ((props.char.joker) ? <TileJokerMarked letter={props.char.data}/> : <TileMarked letter={props.char.data} points={points}/>)
    : ((props.char.joker) ? <TileJoker letter={props.char.data}/> : <Tile letter={props.char.data} points={points}/>);

  return (
    <div className={classes}>
      {maybeMarkedTile}
    </div>
  );
};

const BoardTile = (props) => {
  const pos = {type: props.posType, x: props.x, y: props.y};
  const dispatch = useDispatch();
  const tile = useSelector(state => getTile(state, pos), shallowEqual);

  const [showEmptyTileSelector, setShowEmptyTileSelector] = useState(false);

  const [{isDragging}, drag] = useDrag({
    item: { type: draggableEnum.TILE },
    canDrag: !!tile.currentChar && !showEmptyTileSelector,
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        let newPos = monitor.getDropResult();
        dispatch(moveChar({newPos, pos, char: tile.currentChar}));
      }
    },
  });

  if (isDragging && showEmptyTileSelector) {
    setShowEmptyTileSelector(false);
  }

  const [, drop] = useDrop({
    accept: draggableEnum.TILE,
    drop: () => { return pos; },
    canDrop: (item, monitor) => {
      return tile.playedChar === null;
    },
  });


  const currentTile = (() => {
    if (tile.currentChar !== null) {
      const emptyTileSelector = (tile.currentChar.joker && showEmptyTileSelector)
        ? <EmptyTileSelector hidden={false} pos={pos} letter={tile.currentChar.data} />
        : null;
  
      const cls = classNames({
        'currentTile': true,
        'isDragged': isDragging,
        'onTopOfTile': tile.playedChar !== null,
      })
      return (
        <div className={cls}>
          <TileWrapper char={tile.currentChar} played={false} marked={false} />
          {emptyTileSelector}
        </div>
      );
    }
    return null;
  })();

  const backgroundTile = (() => {
    if (tile.playedChar !== null) {
      return (
        <TileWrapper
          char={tile.playedChar}
          played={true}
          marked={tile.marked}
        />
      );
    }

    return (
      <>
        <TileBackgroundWrapper type={tile.boardTile} posType={props.posType} x={props.x} y={props.y} />
      </>
    );
  })();

  const longpress = (e) => {
    if (isDragging || tile.currentChar === null) {
      return;
    }

    const currentTarget = e.currentTarget;
    const tileClick = e => {
      dispatch(clickTile({pos: pos}));
      e.preventDefault();
    };

    if (pos.type === 'board') {
      currentTarget.addEventListener('click', tileClick, {once: true});
    }
    if (!tile.currentChar.joker) {
      return;
    }

    const preventDefault = (e) => e.preventDefault();

    const timer = setTimeout((e) => {
      setShowEmptyTileSelector(true);
      window.addEventListener('touchmove', preventDefault, { passive: false });
      currentTarget.removeEventListener('click', tileClick);
    }, 600);

    window.addEventListener('mouseup', e => {
      clearTimeout(timer);
      setShowEmptyTileSelector(false);
      window.removeEventListener('touchmove', preventDefault);
    }, {once: true});
    window.addEventListener('touchend', e => {
      const n = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
      const mouseUpEvent = new MouseEvent(
        "mouseup",
        {
          bubbles: true,
          cancelable: true,
          view: window,
        }
      );
      n.dispatchEvent(mouseUpEvent);
    }, {once: true});
  }

  const onMouseOver = (props.onMouseOver !== undefined)
    ? (e) => {
      props.onMouseOver(pos);
      e.preventDefault();
    }
    : null;

  const key = "tile-"+(tile.currentChar ? 'c' : '')+(tile.playedChar ? 'p' : '')+(tile.marked ? 'm' : '')+(tile.boardTile ? tile.boardTile : '')+'-'+pos.x;
  return (
    <div ref={(node) => drag(drop(node))} key={key} onMouseDown={longpress} onTouchStart={longpress} onMouseOver={onMouseOver}>{currentTile}{backgroundTile}</div>
  );
};

export const Tray = (props) => {
  return (
    <>
      <div className="tray" onMouseLeave={props.onMouseLeave}>
        <div className="tileContainer">
          <div>
            {[...Array(14).keys()].map(x => (
              <BoardTile key={"tile-"+x} posType="tray" x={x} onMouseOver={props.onMouseOver} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export const EmptyTileSelector = (props) => {
  const dispatch = useDispatch();
  const ref = useRef(null);

  const width = 8;
  const letters = useSelector(getDefLetters);
  const maxY = Math.ceil(letters.length / width);

  let letterPos = {x: 0, y: 0};
  const rows = [];
  for (let y = 0; y < maxY; y++) {
    const columns = [];
    for (let x = 0; x < width; x++) {
      const letter = letters[y * width + x] ?? null;

      if (props.letter === letter) {
        letterPos = {x, y};
      }

      if (letter === null) {
        columns.push(
          <div key={x}></div>
        );
      }  else {
        columns.push(
          <div onMouseUp={(e) => { dispatch(gameSlice.actions.setJoker({pos: props.pos, letter: letter})); }} key={x}>
            <TileWrapper
              char={{data: letter, joker: true}}
              played={true}
              marked={false}
            />
          </div>
        );
      }
    }
    rows.push(<div key={y}>{columns}</div>);
  }

  const setRef = useCallback(node => {
    if (node) {
      const parentRect = node.parentNode.getBoundingClientRect();
      node.style.width = (parentRect.width * width)+'px';
      const childRect = node.getBoundingClientRect();

      let top = parentRect.top-(letterPos.y*parentRect.height);
      let left = parentRect.left-(letterPos.x*parentRect.width);
      if (top < 0) {
        top = 0;
      } else {
        const height = childRect.bottom - childRect.top;
        if (top + height > window.innerHeight) {
          top = window.innerHeight - height;
        }
      }

      if (left < 0) {
        left = 0;
      } else {
        const width = childRect.right - childRect.left;
        if (left + width > window.innerWidth) {
          left = window.innerWidth - width;
        }
      }

      node.style.top = top+'px';
      node.style.left = left+'px';
    }

    // Save a reference to the node
    ref.current = node
  }, [letterPos])

  const cls = classNames({
    'tileContainer': true,
    'emptyTileSelector': true,
    'hidden': props.hidden,
  });
  return <div className={cls} ref={(node) => setRef(node)}>{rows}</div>;
};

export const Board = (props) => {
  const size = useSelector(state => getSize(state), shallowEqual);

  return (
    <div className="tileContainer board" onMouseLeave={props.onMouseLeave}>
      {[...Array(size.height).keys()].map(y => (
        <div key={"row-"+y}>{[...Array(size.width).keys()].map(x => (
          <BoardTile key={"tile-"+x} posType="board" x={x} y={y} onMouseOver={props.onMouseOver} />
        ))}</div>
      ))}
    </div>
  );
};

const GameAreaButtons = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canPlayTiles = useSelector(getCanPlayTiles);
  const canMoveTiles = useSelector(getCanMoveTiles);
  const existsCurrentTiles = useSelector(getExistsCurrentChars);
  const showLeave = useSelector(getShowLeave);
  const isPaused = useSelector(getPaused);

  const [showExtraButtons, setShowExtraButtons] = useState(false);

  if (showLeave) {
    return (
      <div className="buttons">
        <Button onClick={(e) => dispatch({ type: 'action/quitgame' })}>{t('BUTTON.LEAVE')}</Button>
      </div>
    );
  }

  const extraButtons = (
    <div className="extraButtons">
      <div className="connection"/>
      <Button onClick={(e) => dispatch({ type: 'action/pause' })} disabled={!canMoveTiles}>{isPaused ? t('BUTTON.UNPAUSE') : t('BUTTON.PAUSE')}</Button>
      <Button onClick={(e) => dispatch(viewSlice.actions.addDialog({type: DialogType.CONFIRM_GIVE_UP}))} disabled={!canMoveTiles}>{t('BUTTON.GIVE_UP')}</Button>
    </div>
  );

  const extraButtonCls = classNames({
    'open': showExtraButtons,
  });

  return (
    <div className="buttons">
      <Button onClick={(e) => dispatch({ type: 'action/playTiles' })} disabled={!canPlayTiles || !existsCurrentTiles}>{t('BUTTON.PLAY')}</Button>
      <Button onClick={(e) => dispatch(cleanTiles())} disabled={!canMoveTiles || !existsCurrentTiles}>{t('BUTTON.CLEAN')}</Button>
      <Button onClick={(e) => dispatch({ type: 'action/swapTiles' })} disabled={!canPlayTiles || !existsCurrentTiles}>{t('BUTTON.SWAP')}</Button>
      <Button onClick={(e) => dispatch(shuffleTiles())} disabled={!canMoveTiles}>{t('BUTTON.SHUFFLE')}</Button>
      <Button onClick={(e) => dispatch({ type: 'action/pass' })} disabled={!canPlayTiles}>{t('BUTTON.PASS')}</Button>
      <div className="openExtraWrapper">
        {showExtraButtons ? extraButtons : null}
        <Button onClick={(e) => setShowExtraButtons(!showExtraButtons)} className={extraButtonCls}>{showExtraButtons ? "▼" : "▲"}</Button>
      </div>
    </div>
  );
};

export const GameArea = () => {
  const dispatch = useDispatch();

  let currentPos = null;
  const keydown = (e) => {
    if (currentPos !== null) {
      dispatch(keypress({pos: currentPos, char: e.key}));
    }
  };

  const mouseOver = (pos) => {
    currentPos = pos;
  };

  const mouseLeave = (e) => {
    currentPos = null;
  };

  useEffect(() => {
    window.addEventListener('keypress', keydown);
    return () => {
      window.removeEventListener('keypress', keydown);
    };
  });

  return (
    <div className="gameArea">
      <TileDefs/>
      <div className="boardWrapper">
        <Board onMouseLeave={mouseLeave} onMouseOver={mouseOver}/>
      </div>
      <Tray onMouseLeave={mouseLeave} onMouseOver={mouseOver}/>
      <GameAreaButtons/>
    </div>
  );
};

const formatTime = (totalSeconds) => {
  const negative = totalSeconds < 0;
  totalSeconds = Math.abs(totalSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return (negative ? '-' : '') + minutes+":"+((seconds < 10) ? "0"+seconds : seconds);
}

const CountdownTime = (props) => {
  if (props.time === null) {
    return null;
  }
  const cls = classNames({
    'blink': props.time < 10,
  });
  return (
    <span className={cls}>{formatTime(props.time)}</span>
  )
}

const GamePlayer = (props) => {
  const player = useSelector(state => getPlayer(state, props.no), shallowEqual);
  const currentPlayer = useSelector(getCurrentPlayerNo);

  const cls = classNames({
    'current': props.no === currentPlayer,
  });
  return (
    <tr className={cls}>
      <td>{player.name}</td><td>{player.points}</td><td><CountdownTime time={player.turnTimeLeft}/></td><td><CountdownTime time={player.totalTimeLeft}/></td><td>{player.tilesLeft}</td>
    </tr>
  );
}

export const GamePlayerList = () => {
  const playerNOs = useSelector(getPlayerNOsSorted, shallowEqual);
  return (
    <table className="gamePlayerList">
      <tbody>
        {playerNOs.map(no => <GamePlayer key={no} no={no}/>)}
      </tbody>
    </table>
  );
};

export const GameTilesLeft = () => {
  const { t } = useTranslation();
  const tilesLeft = useSelector(getTilesLeft);
  return (
    <div className="tilesLeft">
      <Trans t={t} i18nKey="TILES_LEFT" count={tilesLeft}>
        <b>Återstående brickor:</b> {{count: tilesLeft}} st
      </Trans>
    </div>
  )
}

const GameLogMessageWrapper = (props) => {
  const msg = useSelector(state => getLogMessage(state, props.id), shallowEqual);
  const cls = classNames({
    'auto': msg.type !== messageEnum.MESSAGE,
    'message': msg.type === messageEnum.MESSAGE,
  })
  return (
    <div className={cls}>{msg.type !== messageEnum.MESSAGE ? "» " : ""}<Message type={msg.type} args={msg.args}/></div>
  );
}

export const GameLog = () => {
  const num = useSelector(numLogMessages);
  let ids = [...Array(num).keys()];
  if (isMobile) {
    return (
      <div className="gameLog box">
        {ids.reverse().map(id => (
          <GameLogMessageWrapper key={id} id={id}/>
        ))}
      </div>
    );
  }

  return (
    <ScrollToBottom className="gameLog box">
      {ids.map(id => (
        <GameLogMessageWrapper key={id} id={id}/>
      ))}
    </ScrollToBottom>
  );
};

export const GameChat = () => {
  const dispatch = useDispatch();
  const localPlayerNo = useSelector(getLocalPlayerNo);
  return (
    <>
      <GameLog/>
      <ChatSubmit onSubmit={msg => dispatch({ type: 'action/gameMessage', payload: {data: msg} })} disabled={localPlayerNo === null}/>
    </>
  );
}
